import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";

import { LOADING_STATUSES as STATUS } from "constants/common";
import { formatDate } from "helpers/format-date";
import {
  loadEmailThread,
  clearSelectedThread,
  getSelectedThread,
  getSelectedThreadStatus,
} from "redux/slices/central-email-threads";
import EmailMessage from "./email-message";
import ThreadAssignee from "./thread-assignee";

const EMAIL_TYPE = "central_email_message";
const ATTACHMENT_TYPE = "central_email_attachment";
const ATTACHMENT_RELATIONSHIP = "central_email_attachments";

// The API is inconsistent about separators in resource types, so compare
// normalised, the same guard the phone call modal uses.
const normaliseType = (type) => String(type || "").replace(/-/g, "_");

// Relationship names drift the same way the types do, so the key is looked up
// normalised rather than read straight off the object.
const relatedIds = (item, name) => {
  const relationships = item?.relationships || {};
  const key = Object.keys(relationships).find(
    (candidate) => normaliseType(candidate) === name
  );

  return relationships[key]?.data || [];
};

const Thread = () => {
  const dispatch = useDispatch();
  const { threadId } = useParams();

  const thread = useSelector(getSelectedThread);
  const status = useSelector(getSelectedThreadStatus);

  useEffect(() => {
    if (threadId) dispatch(loadEmailThread(threadId));

    return () => {
      dispatch(clearSelectedThread());
    };
  }, [dispatch, threadId]);

  if (status === STATUS.loading) {
    return (
      <div className="inboxThreadPane">
        <div className="inboxThreadLoader">
          <TailSpin
            width={60}
            height={60}
            color="#E0E0E0"
            ariaLabel="loading"
          />
        </div>
      </div>
    );
  }

  if (status === STATUS.failed) {
    return (
      <div className="inboxThreadPane">
        <div className="inboxEmptyState">This email could not be loaded</div>
      </div>
    );
  }

  const attrs = thread?.data?.attributes;
  if (!attrs) return <div className="inboxThreadPane" />;

  const included = thread?.included ?? [];

  const emails = included.filter(
    (item) => normaliseType(item.type) === EMAIL_TYPE
  );

  // A thread hands back the attachments of every email in the one included
  // array, so unlike a single-parent response they can only be told apart by
  // the relationship listing on the email that owns them.
  const attachmentsOf = (email) =>
    relatedIds(email, ATTACHMENT_RELATIONSHIP)
      .map(({ id }) =>
        included.find(
          (item) =>
            normaliseType(item.type) === ATTACHMENT_TYPE &&
            String(item.id) === String(id)
        )
      )
      .filter(Boolean);

  const sortedEmails = [...emails].sort((a, b) => {
    const left = a.attributes?.sent_at || a.attributes?.created_at || "";
    const right = b.attributes?.sent_at || b.attributes?.created_at || "";

    return new Date(left) - new Date(right);
  });

  return (
    <div className="inboxThreadPane">
      <div className="inboxThreadHeader">
        <div className="inboxThreadHeaderText">
          <h2 className="inboxThreadSubject">
            {attrs.subject || "(no subject)"}
          </h2>
          <div className="inboxThreadMeta">
            {attrs.from_email}
            {attrs.created_at &&
              ` · Created ${formatDate(attrs.created_at, "yyyy-MM-dd HH:mm")}`}
            {` · ${sortedEmails.length} ${
              sortedEmails.length === 1 ? "email" : "emails"
            }`}
          </div>
        </div>
        <ThreadAssignee
          threadId={thread.data.id}
          supportAgentId={attrs.support_agent_id}
        />
      </div>
      <div className="inboxThreadBody">
        {!sortedEmails.length && (
          <div className="inboxEmptyState">
            This thread has no emails stored yet
          </div>
        )}
        {sortedEmails.map((email) => (
          <EmailMessage
            key={email.id}
            email={email}
            attachments={attachmentsOf(email)}
          />
        ))}
      </div>
    </div>
  );
};

export default Thread;
